import React from 'react';
import { Navigate } from 'react-router-dom';

// Lazy-load pages for code-splitting
const DashboardPage = React.lazy(() => import('../app/(admin)/dashboard/page'));
const UsersPage = React.lazy(() => import('../app/(admin)/users/page'));
const UserDetailPage = React.lazy(() => import('../app/(admin)/users/[id]/page'));
const TransactionsPage = React.lazy(() => import('../app/(admin)/transactions/page'));
const LedgerPage = React.lazy(() => import('../app/(admin)/ledger/page'));
const PayoutsPage = React.lazy(() => import('../app/(admin)/payouts/page'));
const PhysicalRedemptionsPage = React.lazy(() => import('../app/(admin)/physical-redemptions/page'));
const ReconciliationPage = React.lazy(() => import('../app/(admin)/reconciliation/page'));
const InvestmentsPage = React.lazy(() => import('../app/(admin)/investments/page'));
const RewardsPage = React.lazy(() => import('../app/(admin)/rewards/page'));
const SandukCoinsPage = React.lazy(() => import('../app/(admin)/rewards/sanduk-coins/page'));
const RbacPage = React.lazy(() => import('../app/(admin)/rbac/page'));
const AdminUserDetailPage = React.lazy(() => import('../app/(admin)/rbac/admin-users/[id]/page'));
const AuditLogsPage = React.lazy(() => import('../app/(admin)/audit-logs/page'));
const CMSPage = React.lazy(() => import('../app/(admin)/cms/page'));
const OffersAdminPage = React.lazy(() => import('../app/(admin)/offers/page'));
const SupportPage = React.lazy(() => import('../app/(admin)/support/page'));
const ReferralsPage = React.lazy(() => import('../app/(admin)/referrals/page'));
const BankStatementPage = React.lazy(() => import('../app/(admin)/bank-statement/page'));
const NotificationsPage = React.lazy(
  () => import('../app/(admin)/notifications/page'),
);
const NotificationCampaignsPage = React.lazy(
  () => import('../app/(admin)/notifications/campaigns/page'),
);
const NotificationCreateCampaignPage = React.lazy(
  () => import('../app/(admin)/notifications/create-campaign/page'),
);
const NotificationTemplatesPage = React.lazy(
  () => import('../app/(admin)/notifications/templates/page'),
);
const NotificationCampaignDetailsPage = React.lazy(
  () => import('../app/(admin)/notifications/campaigns/[id]/page'),
);
const NotificationCampaignRecipientsPage = React.lazy(
  () => import('../app/(admin)/notifications/campaigns/[id]/recipients/page'),
);
const WhatsAppSetupGate = React.lazy(() => import('../components/whatsapp/WhatsAppSetupGate'));
const WhatsAppDashboardPage = React.lazy(() => import('../app/(admin)/whatsapp/dashboard/page'));
const WhatsAppTemplatesPage = React.lazy(() => import('../app/(admin)/whatsapp/templates/page'));
const WhatsAppTemplateCreatePage = React.lazy(
  () => import('../app/(admin)/whatsapp/templates/create/page'),
);
const WhatsAppCampaignsPage = React.lazy(() => import('../app/(admin)/whatsapp/campaigns/page'));
const WhatsAppCampaignSendPage = React.lazy(
  () => import('../app/(admin)/whatsapp/campaigns/send/page'),
);
const WhatsAppCampaignDetailPage = React.lazy(
  () => import('../app/(admin)/whatsapp/campaigns/[id]/page'),
);
const WhatsAppInboxPage = React.lazy(() => import('../app/(admin)/whatsapp/inbox/page'));
const WhatsAppPhonebooksPage = React.lazy(() => import('../app/(admin)/whatsapp/phonebooks/page'));
const WhatsAppPhonebookDetailPage = React.lazy(
  () => import('../app/(admin)/whatsapp/phonebooks/[id]/page'),
);
const WhatsAppSettingsPage = React.lazy(() => import('../app/(admin)/whatsapp/settings/page'));

/**
 * Admin route definitions.
 * Each entry maps to a <Route> rendered inside AdminLayout's <Outlet>.
 */
export const adminRoutes = [
  {
    path: 'dashboard',
    element: <DashboardPage />,
  },
  {
    path: 'support',
    element: <SupportPage />,
  },
  {
    path: 'support/:ticketId',
    element: <SupportPage />,
  },
  {
    path: 'users',
    element: <UsersPage />,
  },
  {
    path: 'referrals',
    element: <ReferralsPage />,
  },
  {
    path: 'bank-statement',
    element: <BankStatementPage />,
  },
  {
    path: 'users/:id',
    element: <UserDetailPage />,
  },
  {
    path: 'transactions',
    element: <TransactionsPage />,
  },
  {
    path: 'ledger',
    element: <LedgerPage />,
  },
  {
    path: 'payouts',
    element: <PayoutsPage />,
  },
  {
    path: 'physical-redemptions',
    element: <PhysicalRedemptionsPage />,
  },
  {
    path: 'reconciliation',
    element: <ReconciliationPage />,
  },
  {
    path: 'investments',
    element: <InvestmentsPage />,
  },
  {
    path: 'rewards/sanduk-coins',
    element: <SandukCoinsPage />,
  },
  {
    path: 'rewards',
    element: <RewardsPage />,
  },
  {
    path: 'rbac',
    element: <RbacPage />,
  },
  {
    path: 'rbac/admin-users/:id',
    element: <AdminUserDetailPage />,
  },
  {
    path: 'audit-logs',
    element: <AuditLogsPage />,
  },
  {
    path: 'cms',
    element: <CMSPage />,
  },
  {
    path: 'offers',
    element: <OffersAdminPage />,
  },
  {
    path: 'notifications',
    element: <NotificationsPage />,
  },
  {
    path: 'notifications/campaigns',
    element: <NotificationCampaignsPage />,
  },
  {
    path: 'notifications/campaigns/create',
    element: <NotificationCreateCampaignPage />,
  },
  {
    path: 'notifications/campaigns/:id',
    element: <NotificationCampaignDetailsPage />,
  },
  {
    path: 'notifications/campaigns/:id/recipients',
    element: <NotificationCampaignRecipientsPage />,
  },
  {
    path: 'notifications/templates',
    element: <NotificationTemplatesPage />,
  },
  {
    path: 'whatsapp',
    element: <WhatsAppSetupGate />,
    children: [
      { path: 'dashboard', element: <WhatsAppDashboardPage /> },
      { path: 'templates', element: <WhatsAppTemplatesPage /> },
      { path: 'templates/create', element: <WhatsAppTemplateCreatePage /> },
      { path: 'campaigns', element: <WhatsAppCampaignsPage /> },
      { path: 'campaigns/all', element: <Navigate to="/whatsapp/campaigns" replace /> },
      { path: 'campaigns/new', element: <Navigate to="/whatsapp/campaigns/send" replace /> },
      { path: 'campaigns/send', element: <WhatsAppCampaignSendPage /> },
      { path: 'campaigns/:id', element: <WhatsAppCampaignDetailPage /> },
      { path: 'inbox', element: <WhatsAppInboxPage /> },
      { path: 'inbox/:chatId', element: <WhatsAppInboxPage /> },
      { path: 'phonebooks', element: <WhatsAppPhonebooksPage /> },
      { path: 'phonebooks/:id', element: <WhatsAppPhonebookDetailPage /> },
      { path: 'settings', element: <WhatsAppSettingsPage /> },
    ],
  },
];
